import { analytics, performance } from "../../../firebase.ts";
import { trace, PerformanceTrace } from "firebase/performance";
import { logEvent, setUserProperties } from "firebase/analytics";

import Dexie from "dexie";
import template from "./sw-menu.mjs";
import { render } from "lit-html";
import Strings from "../../../models/Strings.json";
import { LANGUAGE, isApple, isProduction } from "../../global.ts";
import { MAJOR_VERSION } from "../../../sw3.ts";
import { SwToc } from "../sw-toc/sw-toc.ts";
import { HeartBank } from "../../../managers/HeartBank.ts";

const strings: any = Strings;

export class SwMenu extends HTMLElement {
    private heartbank = new HeartBank();
    private componentTrace: PerformanceTrace;
    private drawer: HTMLElement;
    private donate: HTMLElement;
    private monk: HTMLImageElement;
    private reset: HTMLElement;
    private notifications: HTMLAnchorElement;

    private button1: HTMLButtonElement;
    private button2: HTMLButtonElement;
    private button3: HTMLButtonElement;
    
    private basket1 = false;
    private basket2 = false;
    private basket3 = false;
    

    constructor() {
        super();
        this.componentTrace = trace(performance, "component");
        this.componentTrace.putAttribute("view", "sw-menu");
        this.componentTrace.start();
        render(template, this);

        this.drawer = this.querySelector("#sw-menu-hide")!;
        this.donate = this.querySelector("#sw-menu-donate")!;
        this.monk = this.querySelector("#sw-menu-monk")!;
        this.reset = this.querySelector("#sw-menu-reset")!;
        this.notifications = this.querySelector("#sw-menu-notifications")!;

        this.button1 = this.querySelector("#sw-menu-basketBtn-0")!;
        this.button2 = this.querySelector("#sw-menu-basketBtn-1")!;
        this.button3 = this.querySelector("#sw-menu-basketBtn-2")!;
    }

    connectedCallback() {
        //this.youtube();

        this.notifications.addEventListener("click", async () => {
            if (confirm(strings.notifications2[LANGUAGE])) {
                await this.heartbank.saveTokenToFirestore();
                alert(strings.notifications3[LANGUAGE]);
                this.heartbank.alertAdmin("notifications/yes");
            }
        });

        this.button1.addEventListener("click", () => {
            const svg = this.button1.lastElementChild as SVGElement;
            if (this.basket1) {
                svg.classList.remove("rotate-180");
                this.basket1 = false;
            } else {
                svg.classList.add("rotate-180");
                this.basket1 = true;
            }
        });
        this.button2.addEventListener("click", () => {
            const svg = this.button2.lastElementChild as SVGElement;
            if (this.basket2) {
                svg.classList.remove("rotate-180");
                this.basket2 = false;
            } else {
                svg.classList.add("rotate-180");
                this.basket2 = true;
            }
        });
        this.button3.addEventListener("click", () => {
            const svg = this.button3.lastElementChild as SVGElement;
            if (this.basket3) {
                svg.classList.remove("rotate-180");
                this.basket3 = false;
            } else {
                svg.classList.add("rotate-180");
                this.basket3 = true;
            }
        });

        this.donate.addEventListener("click", () => this.drawer.click());
        this.monk.addEventListener("click", () => {
            document.location.hash = "#bookmark";
            const toc = document.querySelector("sw-toc") as SwToc;
            toc.show("pali", 1, 1);
            this.drawer.click();
        });
        this.reset.addEventListener("click", this.showUpdatePrompt.bind(this));

        this.componentTrace.incrementMetric("count", 1);
        this.componentTrace.stop();
    }

    private youtube() {
        const a = this.querySelector("#sw-menu-youtube") as HTMLAnchorElement;
        a.href = isApple
            ? "https://www.youtube.com/playlist?list=PLgQIYTIJXpzYIw-cqKczJqEu3WPQ6Pw-3"
            : "https://www.youtube.com/playlist?list=PLgQIYTIJXpzaotVkaGL05TyT_loSdtX2W";
    }

    private async showUpdatePrompt() {
        const userResponse = confirm(strings.refresh[LANGUAGE]);
        if (userResponse) {
            document.body.style.visibility = "invisible";
            this.drawer.click();
            this.clearStorage();
        }
        logEvent(analytics, "update", { response: userResponse });
    }

    private async clearStorage(all: boolean = false) {
        if (isProduction) {
            await this.unregisterAllServiceWorkers();
            logEvent(analytics, "cache", { task: "update" });
            window.location.reload(); //TODO: check bc might be redundant bc browser auto refresh ?
        } else {
            if (all) localStorage.clear();
            await this.unregisterAllServiceWorkers();
            await this.clearCaches(true);
            await this.clearAllDatabases();
            logEvent(analytics, "cache", { task: "reset" });
            console.warn("Local storage deleted.");
            window.location.reload(); // doesn't work after clear indexed; don't know wny
        }
    }

    // TODO: refactor as extension to serviceWorker?? bc duplicate code with menu.ts
    private async unregisterAllServiceWorkers(): Promise<void> {
        if ("serviceWorker" in navigator) {
            try {
                const registrations =
                    await navigator.serviceWorker.getRegistrations();
                for (const registration of registrations) {
                    const success = await registration.unregister();
                    if (success) {
                        console.warn("Service worker unregistered.");
                    } else {
                        console.error(
                            "Failed to unregister the service worker."
                        );
                    }
                }
            } catch (error) {
                console.error(
                    "Error fetching service worker registrations: ",
                    error
                );
            }
        } else {
            console.warn("Service workers are not supported in this browser.");
        }
    }

    private async clearCaches(all: boolean = false) {
        const cacheNames = await caches.keys();
        for (const cacheName of cacheNames) {
            if (all) {
                await caches.delete(cacheName);
            } else {
                if (cacheName !== MAJOR_VERSION) await caches.delete(cacheName);
            }
        }
        console.warn("Caches deleted.");
    }

    async clearAllDatabases(all: boolean = false): Promise<void> {
        try {
            // Get all database names
            const dbNames = all ? await Dexie.getDatabaseNames() : [];
            // [
            //     "Bookmarks",
            //     "Books",
            //     "Dictionaries",
            //     "Transcriptions"
            // ]

            // Delete each database
            for (const dbName of dbNames) {
                await Dexie.delete(dbName);
                console.warn(`Deleted database: ${dbName}`);
            }

            console.log("All databases have been cleared.");
        } catch (error: any) {
            console.error("Error clearing databases: ", error.message);
        }
    }

    // deprecated
    private async clearAllIndexedDB(): Promise<void> {
        try {
            const databases = await (indexedDB.databases
                ? indexedDB.databases()
                : Promise.resolve([]));

            if (databases.length === 0) {
                console.warn("No IndexedDB databases found.");
                return;
            }

            for (const dbInfo of databases) {
                if (dbInfo.name) {
                    const deleteRequest = indexedDB.deleteDatabase(dbInfo.name);
                    await new Promise<void>((resolve, reject) => {
                        deleteRequest.onsuccess = () => {
                            console.warn(`Database "${dbInfo.name}" deleted.`);
                            resolve();
                        };
                        deleteRequest.onerror = (event) => {
                            console.error(
                                `Failed to delete database "${dbInfo.name}": `,
                                event
                            );
                            reject(event);
                        };
                    });
                }
            }
        } catch (error) {
            console.error("Error clearing IndexedDB databases: ", error);
        }
    }
}
